import React, { useState, useEffect } from 'react';
import { getJasaLayanan, createJasaLayanan, updateJasaLayanan, deleteJasaLayanan, importJasaLayanan } from '../api';
import { Plus, Edit2, Trash2, X, Search, Wrench, Upload, Download } from 'lucide-react';

export default function JasaLayanan() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: '', harga_jual: 0, satuan: 'Jasa', keterangan: '', point: 0 });

  const loadData = () => {
    setLoading(true);
    getJasaLayanan()
      .then(res => setData(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  // Auto-refresh saat tab aktif kembali
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ nama: '', harga_jual: 0, satuan: 'Jasa', keterangan: '', point: 0 });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      nama: item.nama,
      harga_jual: item.harga_jual,
      satuan: item.satuan || 'Jasa',
      keterangan: item.keterangan || '',
      point: item.point || 0,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nama.trim()) return alert('Nama jasa layanan harus diisi');
    try {
      if (editing) {
        await updateJasaLayanan(editing.id, {
          kode: editing.kode,
          nama: form.nama,
          harga_jual: parseFloat(form.harga_jual) || 0,
          satuan: form.satuan || 'Jasa',
          keterangan: form.keterangan,
          point: parseInt(form.point) || 0,
        });
      } else {
        await createJasaLayanan({
          nama: form.nama,
          harga_jual: parseFloat(form.harga_jual) || 0,
          satuan: form.satuan || 'Jasa',
          keterangan: form.keterangan,
          point: parseInt(form.point) || 0,
        });
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      alert('Gagal menyimpan: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDownloadTemplate = () => {
    // Generate CSV template (no dependencies needed)
    const BOM = '\uFEFF';
    const csv = BOM + 'Kode,Nama Jasa,Harga Jual,Satuan,Keterangan\n' +
      ',Jasa Pemasangan AC,150000,Jasa,Pemasangan AC split 1 PK\n' +
      ',Jasa Bongkar AC,75000,Jasa,Bongkar AC semua jenis\n' +
      ',Jasa Perbaikan Mesin,200000,Jasa,Perbaikan mesin diesel\n' +
      ',Jasa General Check,50000,Jasa,Pemeriksaan umum kendaraan\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_jasa_layanan.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Import data dari "${file.name}"? Data baru akan ditambahkan.`)) return;
    try {
      const res = await importJasaLayanan(file);
      const result = res.data;
      alert(`Import selesai!\nTotal: ${result.total}\nBerhasil: ${result.imported}\nSkipped: ${result.skipped}${result.errors?.length ? '\nError: ' + result.errors.join(', ') : ''}`);
      loadData();
    } catch (err) {
      alert('Gagal import: ' + (err.response?.data?.error || err.message));
    }
    // Reset file input
    e.target.value = '';
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Hapus jasa "${item.nama}"?`)) return;
    try {
      await deleteJasaLayanan(item.id);
      loadData();
    } catch (err) {
      alert('Gagal menghapus: ' + (err.response?.data?.error || err.message));
    }
  };

  const filtered = data.filter(item =>
    !search || item.nama.toLowerCase().includes(search.toLowerCase()) ||
    item.kode.toLowerCase().includes(search.toLowerCase())
  );

  const formatRupiah = (num) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Master Jasa Layanan</h1></div>
        <div className="loading-spinner" style={{ textAlign: 'center', padding: '40px' }}>Memuat data...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-left">
          <h1><Wrench size={24} style={{ marginRight: 8 }} />Master Jasa Layanan</h1>
          <p className="page-subtitle">Daftar jasa yang dapat dijual di menu CM (pemasangan, bongkar, dll)</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={handleDownloadTemplate} title="Download template Excel">
            <Download size={16} /> Template
          </button>
          <label className="btn btn-outline" style={{ cursor: 'pointer' }}>
            <Upload size={16} /> Import Excel
            <input
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleImport}
            />
          </label>
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={16} /> Tambah Jasa
          </button>
        </div>
      </div>

      <div className="toolbar" style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <div className="search-box" style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
          <input
            type="text"
            className="form-control"
            placeholder="Cari jasa..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
        </div>
        <span style={{ color: '#888', fontSize: 13 }}>{filtered.length} item</span>
      </div>

      <div className="table-wrapper" style={{ margin: '0 -24px', width: 'calc(100% + 48px)' }}>
        <table className="data-table" style={{ marginBottom: 0 }}>
          <thead>
            <tr>
              <th>Kode</th>
              <th>Nama Jasa</th>
              <th>Satuan</th>
              <th>Harga Jual</th>
              <th>Point</th>
              <th>Status</th>
              <th style={{ width: 100 }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#888' }}>Belum ada data jasa layanan</td></tr>
            ) : (
              filtered.map(item => (
                <tr key={item.id}>
                  <td><span className="badge badge-info">{item.kode}</span></td>
                  <td><strong>{item.nama}</strong></td>
                  <td>{item.satuan}</td>
                  <td>{formatRupiah(item.harga_jual)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="badge badge-point" style={{ background: item.point > 0 ? '#ff9800' : '#e0e0e0', color: item.point > 0 ? '#fff' : '#666', fontSize: 13, fontWeight: 700, minWidth: 36 }}>
                      {item.point || 0}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${item.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {item.is_active ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </td>
                  <td>
                    <div className="action-btns" style={{ justifyContent: 'flex-start' }}>
                      <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}><Edit2 size={14} /></button>
                      <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(item)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Create/Edit */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480, background: '#fff' }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Jasa Layanan' : 'Tambah Jasa Layanan'}</h3>
              <button className="btn btn-sm btn-outline" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nama Jasa *</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.nama}
                  onChange={e => setForm({ ...form, nama: e.target.value })}
                  placeholder="Contoh: Jasa Pemasangan AC"
                  autoFocus
                />
              </div>
              <div className="form-row" style={{ display: 'flex', gap: 12 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Harga Jual</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.harga_jual === '' ? '' : Number(form.harga_jual).toLocaleString('id-ID')}
                    onChange={e => {
                      const raw = e.target.value.replace(/[^\d]/g, '');
                      setForm({ ...form, harga_jual: raw === '' ? '' : Number(raw) });
                    }}
                    placeholder="Rp 0"
                    inputMode="numeric"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Satuan</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.satuan}
                    onChange={e => setForm({ ...form, satuan: e.target.value })}
                    placeholder="Jasa"
                  />
                </div>
              </div>
              <div className="form-group">                <div className="form-group">
                  <label>Point <span style={{ fontWeight: 400, color: '#888', fontSize: 12 }}>(untuk KPI teknisi)</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.point === '' ? '' : Number(form.point).toLocaleString('id-ID')}
                    onChange={e => {
                      const raw = e.target.value.replace(/[^\d]/g, '');
                      setForm({ ...form, point: raw === '' ? '' : Number(raw) });
                    }}
                    placeholder="0"
                    inputMode="numeric"
                    style={{ maxWidth: 140 }}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Keterangan</label>
                <textarea
                  className="form-control"
                  value={form.keterangan}
                  onChange={e => setForm({ ...form, keterangan: e.target.value })}
                  placeholder="Deskripsi jasa (opsional)"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editing ? 'Simpan Perubahan' : 'Tambah Jasa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
